import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderFooterLayout from '../components/header-footer-layout';
import Header from '../components/header';
import ErrorMessage from '../components/error-message';
import { scrollToTop } from '../utils/helpers';
import { createEmptyPersonalInfo, type ExistingRepairBooking } from '../models/repair-bookings';
import DateSelection from '../pages/date-selection';
import TimeSelection from '../pages/time-selection';
import PersonalInformationEntry from '../pages/personal-information-entry';
import FinalConfirmation from '../pages/final-confirmation';
import InvalidNavigation from '../pages/invalid-navigation';
import useBookingEditPages from '../hooks/use-booking-edit-pages';
import useBookingState from '../hooks/use-booking-state';
import useCustomerOptions from '../hooks/use-customer-options';
import ShopSelection from '../pages/shop-selection';
import useBooking from '../hooks/use-booking';
import ConsoleLogoutPopup from '../pages/console-logout-popup';
import ConsoleCompletion from '../pages/console-completion';
import LoadingFallback from '../components/loading-fallback';
import { updateConsoleBooking, fetchTimeSlots } from '../utils/api';
import { consoleLogout } from '../utils/paths';

const BookingConsoleEditFlow = () => {
    const navigate = useNavigate();
    const params = useParams<{ token: string }>();
    const csrfToken = window.preloadedState.environment.csrfToken;
    const bookingToken = params.token ?? '';
    const { confirmationId, shop, slot, personalInfo, error, loading } = useBooking(
        csrfToken,
        bookingToken
    );
    const { booking, actions } = useBookingState<ExistingRepairBooking>(() => ({
        type: 'existing',
        shop: shop,
        day: '',
        year: 0,
        selectedSlot: slot,
        personalInfo: personalInfo ?? createEmptyPersonalInfo(),
        bookingToken: bookingToken,
        confirmationId: confirmationId,
    }));
    const { lineStatuses, error: optionsError } = useCustomerOptions(csrfToken);

    const {
        currentPage,
        pageConfig,
        pushPageStack,
        popPageStack,
        complete,
        resetPageStack,
        session,
    } = useBookingEditPages(bookingToken, 'console', csrfToken);
    const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

    useEffect(() => {
        if (loading || !shop || !slot || !confirmationId || !personalInfo) {
            return;
        }
        actions.patchBooking({
            shop,
            selectedSlot: slot,
            personalInfo,
            confirmationId,
        });
    }, [actions, confirmationId, loading, personalInfo, shop, slot]);

    useEffect(() => {
        scrollToTop();
    }, [currentPage]);

    useEffect(() => {
        if (optionsError) {
            alert(optionsError.message);
        }
    }, [optionsError]);

    const resetFlow = () => {
        actions.reset();
        resetPageStack();
    };

    return (
        <HeaderFooterLayout
            title={`【 予約管理 】${pageConfig.title}`}
            onHome={() => {
                navigate('/console/bookings/');
            }}
            onLogout={() => {
                setShowLogoutConfirmation(true);
            }}
            withFooter={false}
        >
            {error && <ErrorMessage message={error.message} />}
            {!error && (
                <>
                    {pageConfig.showShopHeader && <Header title={booking.shop?.name ?? ''} />}
                    {currentPage.status === 'invalid' ? (
                        <InvalidNavigation onClose={resetFlow} />
                    ) : !session ? (
                        <LoadingFallback />
                    ) : currentPage.status === 'shop-selection' ? (
                        <ShopSelection
                            session={session}
                            selectedShop={booking.shop}
                            onComplete={(newShop) => {
                                if (newShop) {
                                    actions.setShop(newShop);
                                }
                                pushPageStack({ status: 'date-selection' });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'date-selection' ? (
                        <DateSelection
                            selectedSlot={booking.selectedSlot}
                            fetchTimeSlots={() =>
                                fetchTimeSlots(
                                    session.csrfToken,
                                    booking.shop!.shop_id,
                                    session.sessionId,
                                    booking.bookingToken
                                )
                            }
                            onComplete={(date, slots) => {
                                pushPageStack({
                                    status: 'time-selection',
                                    selectedDate: date,
                                    slots: slots,
                                });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'time-selection' ? (
                        <TimeSelection
                            selectedDate={currentPage.selectedDate}
                            slots={currentPage.slots}
                            onComplete={(slot) => {
                                actions.setSelectedSlot(slot);
                                pushPageStack({ status: 'personal-info' });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'personal-info' ? (
                        <PersonalInformationEntry
                            info={booking.personalInfo}
                            personalInfoConfig={{ granularity: 'extended', lineStatuses }}
                            handleChange={actions.changePersonalInfo}
                            onComplete={() => {
                                pushPageStack({ status: 'confirm' });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'confirm' ? (
                        <FinalConfirmation
                            personalInfoConfig={{ granularity: 'extended', lineStatuses }}
                            booking={booking}
                            onSubmit={async () => {
                                const result = await updateConsoleBooking(
                                    session.csrfToken,
                                    session.sessionId,
                                    booking
                                );
                                complete({
                                    status: 'edit-complete',
                                    confirmationId: result.confirmation_id,
                                });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'edit-complete' ? (
                        <ConsoleCompletion
                            confirmationId={currentPage.confirmationId}
                            onClose={() => {
                                navigate('/console/bookings/');
                            }}
                            onLogout={consoleLogout}
                        />
                    ) : null}
                    {showLogoutConfirmation && (
                        <ConsoleLogoutPopup
                            onOk={() => {
                                consoleLogout();
                                setShowLogoutConfirmation(false);
                            }}
                            onCancel={() => {
                                setShowLogoutConfirmation(false);
                            }}
                        />
                    )}
                </>
            )}
        </HeaderFooterLayout>
    );
};

export default BookingConsoleEditFlow;
